import { Icons } from "assets";
import classNames from "classnames";
import { useFormik } from "formik";
import { useState } from "react";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CustomButton from "shared/components/common/customButton";
import CustomInput from "shared/components/common/customInput";
import CustomPhoneInput from "shared/components/common/customPhoneInput";
import { toastMessage } from "shared/components/common/toast";
import SuccessModal from "shared/modal/success";
import { routeConstant } from "shared/routes/routeConstant";
import { AddOrganizationService } from "shared/services/organization";
import { AddOrganizationVS } from "shared/utils/validation";
import styles from "./style.module.scss";
import { GetCountService } from "shared/services/general";
import { setCountReducer } from "shared/redux/reducers/count";

const initialValues: OrganizationType = {
  name: "",
  email: "",
  address: "",
  contactNo: "",
  users: [
    {
      name: "",
      email: "",
      role: "",
    },
  ],
};

const AddOrganization = () => {
  const {
    theme: { theme },
  } = useSelector((state: any) => state?.root);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showSuccessModal, setSuccessModal] = useState<boolean>(false);

  const handleShowSuccessModal = () => {
    setSuccessModal(true);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(false);
    navigate(routeConstant.organizations.path, { replace: true });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: AddOrganizationVS,
    onSubmit: () => {
      const emailOccurrences = values.users.reduce((acc: any, user, index) => {
        if (!acc[user.email]) {
          acc[user.email] = [];
        }
        acc[user.email].push(index);
        return acc;
      }, {});

      const duplicatedEmails = Object.keys(emailOccurrences).filter(
        (email) => emailOccurrences[email].length > 1
      );

      if (duplicatedEmails.length > 0) {
        duplicatedEmails.forEach((email) => {
          emailOccurrences[email].forEach((index: number) => {
            formik.setFieldError(
              `users[${index}].email`,
              "Email already in use"
            );
          });
        });
        setSubmitting(false);
      } else if (
        !isValidPhoneNumber(values.contactNo ? values.contactNo : "")
      ) {
        setSubmitting(false);
        setFieldError("contactNo", "Invalid Phone Number");
      } else {
        // If no duplicate emails, proceed with form submission logic
        handleAddOrganization();
      }
    },
  });
  const {
    values,
    touched,
    errors,
    isSubmitting,
    handleChange,
    setFieldValue,
    handleSubmit,
    setSubmitting,
    resetForm,
    setFieldError,
  } = formik;

  const handleAddUserForm = () => {
    setFieldValue("users", [
      ...values.users,
      {
        name: "",
        email: "",
        role: "",
      },
    ]);
  };

  const handleRemoveUserForm = (index: number) => {
    const newUsers = [...values.users];
    newUsers.splice(index, 1);
    setFieldValue("users", newUsers);
  };

  const handleGetCount = () => {
    GetCountService()
      .then(({ data }) => {
        dispatch(
          setCountReducer({
            complaints: data?.complaints_count,
            orgs: data?.orgs_count,
            req: data?.req_count,
          })
        );
      })
      .catch((err) => {});
  };

  const handleAddOrganization = () => {
    setSubmitting(true);
    AddOrganizationService(values)
      .then(() => {
        handleShowSuccessModal();
        handleGetCount();
        resetForm();
      })
      .catch((err) => {
        if (err?.response?.data?.errorTpe === 1) {
          toastMessage("error", "User Emails Already Exists");
          let data: any[] = err?.response?.data?.data;
          for (let i = 0; i < values.users?.length; i++) {
            let job = values.users[i];
            if (data.includes(job?.email)) {
              setFieldError(`users[${i}].email`, "Email already in use");
            }
          }
        } else {
          toastMessage("error", err?.response?.data?.error);
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <div className={classNames(styles.mainContainer)}>
      <div className={classNames("flex flex-col w-full gap-6")}>
        <h1>
          Add New <span>Organization</span>
        </h1>
        <div className={classNames("flex flex-col gap-2")}>
          <div className={classNames("flex items-center gap-4")}>
            <CustomInput
              label="Organization Name"
              required
              placeholder="Enter Organization Name"
              value={values.name}
              onChange={handleChange("name")}
              error={
                touched.name &&
                //@ts-ignore
                errors?.name
                  ? errors?.name
                  : ""
              }
            />
            <CustomInput
              label="Email"
              required
              type="email"
              Icon={Icons.Mail}
              placeholder="Enter Email"
              value={values.email}
              onChange={handleChange("email")}
              error={
                touched.email &&
                //@ts-ignore
                errors?.email
                  ? errors?.email
                  : ""
              }
            />
          </div>
          <div className={classNames("flex items-center gap-4")}>
            <CustomInput
              label="Address"
              required
              placeholder="Enter Address"
              Icon={Icons.Pin}
              value={values.address}
              onChange={handleChange("address")}
              error={
                touched.address &&
                //@ts-ignore
                errors?.address
                  ? errors?.address
                  : ""
              }
            />
            <CustomPhoneInput
              label="Contact Info"
              required
              placeholder="405-235-3313"
              value={values.contactNo}
              onChange={(val) => {
                setFieldValue("contactNo", val);
              }}
              error={
                touched.contactNo &&
                //@ts-ignore
                errors?.contactNo
                  ? errors?.contactNo
                  : ""
              }
            />
          </div>
        </div>
        <h1>
          Add Associated <span>Users</span>
        </h1>
        <div>
          <div
            className={classNames(styles.tableWrapper)}
            onSubmit={(e) => e.preventDefault()}
          >
            <table
              className="table"
              style={{ minWidth: "800px", margin: "0px" }}
            >
              <thead>
                <tr style={{ height: "44px" }}>
                  <th className={classNames(styles.tableHead)}>
                    Full Name <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>
                    Email <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>
                    Role <span>*</span>
                  </th>
                  <th className={classNames(styles.tableHead)}>Action</th>
                </tr>
              </thead>
              <tbody>
                {values.users.map((user, index) => {
                  return (
                    <tr key={index} className="relative">
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          required
                          placeholder="Enter Full Name"
                          value={user.name}
                          onChange={handleChange("users." + index + ".name")}
                          error={
                            touched.users?.[index]?.name &&
                            //@ts-ignore
                            errors.users?.[index]?.name
                          }
                          notRequiredMargin
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          required
                          Icon={Icons.Mail}
                          type="email"
                          placeholder="Enter Email"
                          value={user.email}
                          onChange={(e) => {
                            setFieldValue(
                              `users[${index}].email`,
                              e.currentTarget.value
                            );
                          }}
                          error={
                            touched.users?.[index]?.email &&
                            //@ts-ignore
                            errors.users?.[index]?.email
                          }
                          notRequiredMargin
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "30%" }}
                      >
                        <CustomInput
                          placeholder="Enter Role"
                          value={user.role}
                          onChange={handleChange("users." + index + ".role")}
                          error={
                            touched.users?.[index]?.role &&
                            //@ts-ignore
                            errors.users?.[index]?.role
                          }
                          notRequiredMargin
                        />
                      </td>
                      <td
                        className={classNames(styles.tableContentLabel)}
                        style={{ width: "10%" }}
                      >
                        {values.users.length > 1 ? (
                          <CustomButton
                            title="Remove"
                            Icon={Icons.TrashOutline2}
                            IconDirction="right"
                            containerStyle={classNames(styles.removeBtnStyle)}
                            iconStyle={classNames(styles.iconStyle)}
                            readOnly={values.users.length === 1}
                            onClick={() => {
                              if (values.users.length > 1) {
                                handleRemoveUserForm(index);
                              }
                            }}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <CustomButton
            title="Add More Users"
            Icon={Icons.Plus}
            containerStyle={classNames(
              theme === "dark" ? styles.btnStyle : styles.lighThemeBtnStyle,
              "gap-1 mt-3"
            )}
            IconDirction="left"
            onClick={handleAddUserForm}
          />
        </div>
        <CustomButton
          title="Submit"
          containerStyle={classNames("self-end")}
          onClick={() => {
            handleSubmit();
          }}
          loading={isSubmitting}
          disabled={isSubmitting}
        />
      </div>
      <SuccessModal
        title="Successful Submission. "
        subtitle="Your Organizations has been registered successfully. Happy exploring..!"
        show={showSuccessModal}
        handleClose={handleCloseSuccessModal}
      />
    </div>
  );
};

export default AddOrganization;
